import get from 'lodash/get';
import assign from 'lodash/assign';
import striptags from 'striptags';

import log from '../../../../services/logger_service';
import identityConfig from '../../../../config/identity';
import mergeContent from '../../../../utils/content_util';
import isPaywallEligible from '../../../../utils/post_util';

import {
  buildAssetUrlWithPresets,
  imageSizesLookup,
  changeAssetDomain
} from '../../../../utils/cloudinary_asset_util';

const mapMicYearToDate = year => {
  let finalDate = '2000-01-01T00:00:00Z';

  switch (year) {
    case '2019':
      finalDate = '2019-02-19T11:00:00Z';
      break;

    case '2018':
      finalDate = '2018-02-21T11:00:00Z';
      break;

    case '2017':
      finalDate = '2017-03-17T11:00:00Z';
      break;

    case '2016':
      finalDate = '2016-02-16T11:00:00Z';
      break;

    case '2015':
      finalDate = '2015-02-09T11:00:00Z';
      break;

    case '2014':
      finalDate = '2014-02-10T11:00:00Z';
      break;

    case '2013':
      finalDate = '2013-02-11T11:00:00Z';
      break;

    case '2012':
      finalDate = '2012-02-08T11:00:00Z';
      break;

    case '2011':
      finalDate = '2011-11-30T11:00:00Z';
      break;

    case '2010':
      finalDate = '2010-03-09T11:00:00Z';
      break;

    case '2009':
      finalDate = '2009-02-11T11:00:00Z';
      break;

    case '2008':
      finalDate = '2008-02-15T11:00:00Z';
      break;

    default:
      break;
  }

  return finalDate;
};

/*
Validate your structured data page here (paste in entire view-source: https://search.google.com/structured-data/testing-tool/u/0/
*/
export default function structuredData(
  context,
  location,
  firstLoad,
  unsanitizedURL
) {
  const {
    // authorPageData,
    // categoryPageData,
    // personPageData,
    // tagPageData,
    // videoIndexPageData,
    authorAllFeed,
    categoryHomeList,
    companyPageData,
    giftGuideData,
    homeList,
    ibdPageData,
    jwPlaylistIndex,
    micYearPageData,
    micSectorPageData,
    mic2020YearPage,
    mostCreativePeoplePageData,
    playlistAllFeedData,
    postData,
    productPageData,
    searchPageData,
    staticPageData,
    tagHomeList,
    videoPageData,
    worldChangingIdeasPage,
    hubPage,
    queer50Page,
    micHubPage
  } = context;
  let { type } = context;

  const { config } = context;

  const siteConfig = identityConfig;
  const url = unsanitizedURL.includes('?amp=')
    ? unsanitizedURL.split('?')[0]
    : unsanitizedURL;

  if (siteConfig === undefined) {
    return '';
  }

  const articleType =
    get(postData, 'categories[0].name') === 'News' ? 'NewsArticle' : 'Article';

  let topicList = [];
  let articleTitle = '';
  let authorName = '';
  let articleSection = '';
  let thumbnail = '';
  let articleDate = '';
  let thumbnailCaption;
  let thumbnailWidth;
  let thumbnailHeight;
  let dateModified;
  const DESKTOP_SIZE = true;

  let livePostStructuredData = {};

  if (postData) {
    topicList = get(postData, 'tags');
    articleTitle = get(postData, 'title');
    authorName = get(postData, 'author.name');
    articleSection = get(postData, 'categories[0].slug'); // TODO, needs primary tag if exist
    thumbnail = buildAssetUrlWithPresets(
      changeAssetDomain(get(postData, 'featured_image.source')),
      imageSizesLookup.meta,
      DESKTOP_SIZE
    );

    if (/.gif$/.test(thumbnail)) {
      thumbnail = thumbnail.replace('f_auto', 'f_jpg');
    }

    articleDate = get(postData, 'structuredData.published');
    dateModified = get(postData, 'structuredData.modified');
    thumbnailCaption = striptags(get(postData, 'featured_image.caption') || '');
    thumbnailWidth = get(postData, 'featured_image.width') || 700;
    thumbnailHeight = get(postData, 'featured_image.height') || 700;
  }

  const siteData = {
    humanReadable: 'Fast Company',
    logo: {
      url:
        'https://assets.fastcompany.com/image/upload/v1510092328/fc-logo.png',
      width: '346',
      height: '60'
    }
  };
  const siteName = get(siteData, 'humanReadable');
  const logo = get(siteData, 'logo.url');
  const logoWidth = get(siteData, 'logo.width');
  const logoHeight = get(siteData, 'logo.height');

  const tags = topicList.map(tag => tag.name);
  // Set specific structured data for live posts
  if (postData) {
    const livePosts = get(postData, 'live.posts', []);
    const livePostsLength = livePosts.length;

    if (livePostsLength) {
      type = 'livePost';
      livePostStructuredData = {
        coverageStartTime: get(livePosts[livePostsLength - 1], 'published'),
        coverageEndTime: get(livePosts, '[0].published'),
        liveBlogUpdate: livePosts.map(post => ({
          '@type': 'BlogPosting',
          headline: articleTitle,
          author: {
            '@type': 'Person',
            name: get(post, 'author.name')
          },
          publisher: {
            '@type': 'Organization',
            name: siteName,
            logo: {
              '@type': 'ImageObject',
              url: logo,
              width: logoWidth,
              height: logoHeight
            }
          },
          url: `${url}#update-${get(post, 'id')}`,
          datePublished: get(post, 'published'),
          articleBody: striptags(mergeContent(get(post, 'content', [])))
        }))
      };
    }
  }

  const baseData = {
    '@context': 'https://schema.org'
  };

  const dupeLookUp = {};
  // We need a default date on landing pages in order for parsely to pick them up correctly.
  const defaultParselyDate = '2000-01-01T00:00:00Z';
  const typeLookup = {
    post: {
      '@type': articleType,
      headline: articleTitle,
      url,
      mainEntityOfPage: {
        '@id': url,
        '@type': 'WebPage'
      },
      thumbnailUrl: thumbnail,
      dateCreated: articleDate,
      datePublished: articleDate,
      dateModified,
      articleSection,
      creator: authorName,
      author: {
        '@type': 'Person',
        name: authorName
      },
      keywords: tags,
      image: {
        '@type': 'ImageObject',
        caption: thumbnailCaption,
        url: thumbnail,
        width: thumbnailWidth,
        height: thumbnailHeight
      },
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: logo,
          width: logoWidth,
          height: logoHeight
        }
      }
    },
    livePost: {
      '@type': 'LiveBlogPosting',
      url,
      headline: articleTitle,
      datePublished: articleDate,
      coverageStartTime: get(livePostStructuredData, 'coverageStartTime'),
      coverageEndTime: get(livePostStructuredData, 'coverageEndTime'),
      dateModified,
      publisher: {
        '@type': 'Organization',
        name: siteName,
        logo: {
          '@type': 'ImageObject',
          url: logo,
          width: logoWidth,
          height: logoHeight
        }
      },
      liveBlogUpdate: get(livePostStructuredData, 'liveBlogUpdate')
    },
    homepage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: homeList
        // we can only provide same-origin urls for structured data, so we should filter out
        // any urls that don't share the same domain as current homepage
        .filter(item => {
          // list item url is populated in the url field if it's scheduled in manage
          const listItemUrl = item.url || item.link;

          if (listItemUrl) {
            return listItemUrl.indexOf(url) !== -1;
          }

          return false;
        })
        .map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url:
            item.url ||
            `https://www.fastcompany.com/${get(item, 'id')}/${get(
              item,
              'slug'
            )}`
        }))
    },
    category: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: categoryHomeList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
          `https://www.fastcompany.com/${get(item, 'id')}/${get(item, 'slug')}`
      }))
    },
    tag: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: tagHomeList.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
          `https://www.fastcompany.com/${get(item, 'id')}/${get(item, 'slug')}`
      }))
    },
    author: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: authorAllFeed.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url:
          item.url ||
          `https://www.fastcompany.com/${get(item, 'id')}/${get(item, 'slug')}`
      }))
    },
    personPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: []
    },
    mostCreativePeoplePage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(mostCreativePeoplePageData, 'data.rows', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.fastcompany.com/person/${item.url}`
        })
      )
    },
    ibdPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(ibdPageData, 'data.rows', []).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://www.fastcompany.com/innovation-by-design/2017/category/${
          item.url
        }`
      }))
    },
    micYearPage: {
      '@type': 'Guide',
      name: `Most Innovative Companies ${get(micYearPageData, 'year', '')}`,
      dateCreated: mapMicYearToDate(get(micYearPageData, 'year')),
      datePublished: mapMicYearToDate(get(micYearPageData, 'year')),
      dateModified: mapMicYearToDate(get(micYearPageData, 'year')),
      hasPart: get(micYearPageData, 'data.rows', []).map((item, index) => ({
        '@type': 'Recommendation',
        author: {
          '@type': 'Organization',
          name: 'Fast Company Staff'
        },
        itemReviewed: {
          '@type': 'Organization',
          name: get(item, 'name', `Unknown Item ${index}`),
          position: index + 1,
          url: `https://www.fastcompany.com/company/${item.url}`
        }
      }))
    },
    'wci-list': {
      '@type': 'Guide',
      name: `World Changing Ideas Awards ${get(
        worldChangingIdeasPage,
        'data.acf.franchise_year',
        '2022'
      )}`,
      dateCreated: get(
        worldChangingIdeasPage,
        'data.date',
        '2022-05-03T06:00:41'
      ),
      datePublished: get(
        worldChangingIdeasPage,
        'data.date',
        '2022-05-03T06:00:41'
      ),
      dateModified: get(
        worldChangingIdeasPage,
        'data.date',
        '2022-05-03T06:00:41'
      ),
      hasPart: get(
        worldChangingIdeasPage,
        'data.listData.winners.records',
        []
      ).map((item, index) => ({
        '@type': 'Recommendation',
        author: {
          '@type': 'Organization',
          name: 'Fast Company Staff'
        },
        itemReviewed: {
          '@type': 'Organization',
          name: get(item, 'fields["Company Name"]', `Unknown Item ${index}`),
          // position: index + 1,
          url: get(item, 'fields["Article Link"]', '')
        }
      }))
    },
    queer50Page: {
      '@type': 'Guide',
      name: `${get(
        queer50Page,
        'data.acf.franchise_year',
        '2022'
      )} Fast Company Queer 50 List`,
      dateCreated: '2022-06-08T06:00:41',
      datePublished: '2022-06-08T06:00:41',
      dateModified: '2022-06-08T06:00:41',
      hasPart: get(queer50Page, 'data.externalList.list.records', []).map(
        (item, index) => ({
          '@type': 'Recommendation',
          author: {
            '@type': 'Organization',
            name: 'Fast Company Staff'
          },
          itemReviewed: {
            '@type': 'Person',
            name: get(item, 'fields["Name"]', `Unknown Item ${index}`),
            // position: index + 1,
            url: get(item, 'fields["Article URL"]', '')
          }
        })
      )
    },
    hubPage: {
      '@type': 'ItemList',
      url,
      itemListElement: [
        ...get(hubPage, 'data.featured', []),
        ...get(hubPage, 'data.top', [])
      ].map((item, index) => {
        let bylineAttribution = null;

        get(item, 'tags', []).forEach(tagObj => {
          if (
            tagObj.name.indexOf('byline_') !== -1 ||
            tagObj.slug.indexOf('byline-') !== -1
          ) {
            bylineAttribution = tagObj.name.replace('byline_', '');
          }
        });

        const keywords = [
          ...get(item, 'tags', []),
          ...get(item, 'categories', [])
        ]
          // get everything that is not byline
          .filter(obj => !get(obj, 'name', '').includes('byline'))
          .map(obj => obj.name);

        return {
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'CreativeWork',
            headline: get(item, 'title', `Unknown Headline ${index}`),
            author:
              bylineAttribution ||
              get(item, 'author.name', `Unknown Author ${index}`),
            url: get(item, 'link'),
            datePublished: get(item, 'datePublished'),
            keywords: keywords.join(',')
          }
        };
      })
    },
    micHubPage: {
      '@type': 'CollectionPage',
      name: get(
        micHubPage,
        'data.acf.hub_page.hub_list_headline',
        'Most Innovative Companies Hub'
      ),
      url,
      dateCreated: '2022-04-26T06:00:01',
      datePublished: '2022-04-26T07:00:01',
      dateModified: get(micHubPage, 'data.modified', '2022-05-03T06:00:41'),
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: get(micHubPage, 'data.featuredArticles.posts', []).map(
          (item, index) => ({
            '@type': 'NewsArticle',
            name: get(item, 'title', `Unknown Headline ${index}`),
            image: get(
              item,
              'shareImage',
              'https://images.fastcompany.net/image/upload/v1541101333/fcweb/FC-Twitter-Card_new2_mqyz2q.jpg'
            ),
            url:
              item.link ||
              `https://www.fastcompany.com/most-innovative-companies`
          })
        )
      }
    },
    mic2020YearPage: {
      '@type': 'ItemList',
      name: 'Most Innovative Companies 2020',
      url,
      dateCreated: '2020-03-10T11:00:00Z',
      datePublished: '2020-03-10T11:00:00Z',
      dateModified: '2020-03-10T11:00:00Z',
      itemListElement: get(mic2020YearPage, 'companies', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          name: get(item, 'name', `Unknown Item ${index}`),
          position: index + 1,
          url: `${item.link}`
        })
      )
    },
    micSectorPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(micSectorPageData, 'data.rows', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.fastcompany.com/most-innovative-companies/${get(
            micSectorPageData,
            'data.year'
          )}/sectors/${get(item, 'url')}`
        })
      )
    },
    giftGuidePage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(giftGuideData, 'data.rows', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.fastcompany.com/gift-guide/holiday-2018/category/${
            item.url
          }`
        })
      )
    },
    productPage: {
      '@type': 'NewsArticle',
      headline: get(productPageData, 'data.rows[0].name'),
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      mainEntityOfPage: url
    },
    companyPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(companyPageData, 'data.posts', []).map(
        (item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: item.link
        })
      )
    },
    videoIndexPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(jwPlaylistIndex, 'data.playlists', []).reduce(
        (accum, currVal) => {
          get(currVal, 'playlist', []).forEach(item => {
            const itemUrl = item.link;
            const itemJwId = item.mediaid;

            if (!dupeLookUp.hasOwnProperty(itemJwId)) {
              accum.push({
                '@type': 'ListItem',
                position: accum.length + 1,
                url: itemUrl
              });
              dupeLookUp[itemJwId] = true;
            }
          });
          return accum;
        },
        []
      )
    },
    videoPage: {
      '@type': 'VideoObject',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      name: get(videoPageData, 'data.playlist[0].title', ''),
      description: get(videoPageData, 'data.playlist[0].description', ''),
      thumbnailUrl: buildAssetUrlWithPresets(
        changeAssetDomain(
          get(videoPageData, 'data.playlist[0].featured_image.source', '')
        ),
        imageSizesLookup.meta,
        DESKTOP_SIZE
      ),
      uploadDate: get(
        videoPageData,
        'data.playlist[0].structuredData.published',
        ''
      ),
      transcript: get(videoPageData, 'data.videoSeoText', ''),
      duration: `PT${get(videoPageData, 'data.playlist[0].duration', '')}S`,
      contentUrl: get(videoPageData, 'data.playlist[0].sources[4].file', '')
    },
    videoPlaylistPage: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(playlistAllFeedData, 'data.playlist', []).map(
        (video, index) => {
          const itemUrl = video.link;

          return {
            '@type': 'ListItem',
            position: index + 1,
            url: itemUrl
          };
        }
      )
    },
    search: {
      '@type': 'ItemList',
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      itemListElement: get(searchPageData, 'entry', []).map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: item.link
      }))
    },
    page: {
      '@type': 'NewsArticle',
      headline: get(staticPageData, 'title.rendered'),
      url,
      dateCreated: defaultParselyDate,
      datePublished: defaultParselyDate,
      dateModified: defaultParselyDate,
      mainEntityOfPage: url
    }
  };

  // Custom paywall content identifcation
  const allowPaywallSchema = false;
  let paywallSchema = {};
  if (
    allowPaywallSchema &&
    type === 'post' &&
    postData &&
    isPaywallEligible(postData)
  ) {
    paywallSchema = {
      isAccessibleForFree: 'False',
      hasPart: [
        {
          '@type': 'WebPageElement',
          isAccessibleForFree: 'False',
          cssSelector: '.bp-content'
        }
      ]
    };
  }

  const JSONMeta = JSON.stringify(
    assign(baseData, typeLookup[type], paywallSchema)
  );
  const lookUp = typeLookup[type];
  if (!lookUp) {
    log.error(
      `Structured Data Type Lookup: ${type} not found on url: ${get(
        config,
        'navHistory[0]'
      )}`
    );
  }

  return lookUp
    ? `<script type="application/ld+json">${JSONMeta}</script>`
    : '';
}
